import { Link, useParams } from 'react-router-dom';
import { getBlogPostBySlug, BLOG_POSTS } from '@/data/blogData';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, ArrowLeft, Share2, Printer, CheckCircle2 } from 'lucide-react';
import { 
  createBlogPostingSchema, 
  createBreadcrumbListSchema 
} from '@/utils/schemaGenerator';

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug) || BLOG_POSTS[0];

  const blogPostingSchema = createBlogPostingSchema({
    title: post.title,
    excerpt: post.excerpt,
    slug: post.slug,
    image: post.image,
    author: post.author,
    datePublished: post.date,
    content: post.content
  });

  const breadcrumbSchema = createBreadcrumbListSchema([
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: post.title, url: `/blog/${post.slug}` }
  ]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Article link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead
        title={`${post.title} | Inchpaper Print Blog`}
        description={post.excerpt}
        keywords={post.keywords}
        canonical={`https://print.inchpaper.com/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        schemas={[blogPostingSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-red-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to All Articles</span>
        </Link>

        {/* Article Container */}
        <article className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-10 space-y-8">
          
          {/* Article Header */}
          <header className="space-y-4 border-b border-slate-100 pb-6">
            <span className="inline-block bg-red-100 text-[#7d0909] text-xs font-bold px-3 py-1 rounded-full">
              {post.category}
            </span>

            <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500 pt-2">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5 font-medium text-slate-700">
                  <User className="w-4 h-4 text-[#7d0909]" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>

              <button
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-red-700 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>Share Article</span>
              </button>
            </div>
          </header>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden max-h-96 shadow-xs border border-slate-100">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Body */}
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-sm sm:text-base whitespace-pre-line">
            {post.content}
          </div>

          {/* Print Callout Card */}
          <div className="bg-gradient-to-r from-red-50 to-slate-50 border border-red-200 p-6 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-base">Ready to Print Your Documents?</h3>
              <p className="text-slate-600 text-xs mt-1">
                Upload your files now to experience instant 60-second cloud kiosk printing.
              </p>
            </div>
            <Link to="/#upload">
              <Button className="bg-[#7d0909] hover:bg-[#5e0707] text-white px-6 py-3 rounded-xl font-bold text-xs flex items-center gap-2 shadow-xs shrink-0">
                <Printer className="w-4 h-4" />
                <span>Upload & Print Now</span>
              </Button>
            </Link>
          </div>

        </article>
      </main>

      <Footer />
    </div>
  );
}
